import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import sun from "../../../assets/Images/TempleThankYou/sun.svg";
import skylineLines from "../../../assets/Images/TempleThankYou/skyline-lines.svg";
import skylineFill from "../../../assets/Images/TempleThankYou/skyline-fill.svg";

// Figma "Thank You — Skyline Intro" (3578:7144), five steps played once on load:
//   1 Start  → sun low and faint, nothing drawn
//   2 Draw   → the temple's line art is revealed left to right, birds set off
//   3 Fill   → the coloured temple fades in over the lines, sun rises behind it
//   4 Petals → marigold petals fall through the page
//   5 Rest   → temple + sun stay; birds and petals have faded away
// Everything is positioned in % of the 760×420 temple so it scales with the screen.
// With "reduce motion" on, the Rest state shows straight away.

const W = 760;
const H = 420;

const lineReveal = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to   { clip-path: inset(0 0 0 0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
// Sun: from y 84 to y 4 on the 1440 canvas (80px = 41.7% of its 192px height)
const sunRise = keyframes`
  from { transform: translateY(41.7%) scale(0.9); opacity: 0.35; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
`;
// Birds: x 220 → 560 → 1260 on the canvas (relative to the temple: -120 → 220 → 920)
const birdsFly = keyframes`
  0%   { left: -15.8%; opacity: 0; }
  10%  { opacity: 1; }
  35%  { left: 28.9%; }
  85%  { opacity: 1; }
  100% { left: 121%; opacity: 0; }
`;

// Petal start (x, y above the frame) and rest (x, y near the foot of the hero), from steps 1 and 5
const PETALS = [
  { x: 300, y: -20, ex: 259, ey: 1134, w: 6.4, h: 9.6, rot: 100, color: "#EF9F27" },
  { x: 467, y: -34, ex: 497, ey: 1148, w: 8, h: 12, rot: 53, color: "#E24B4A" },
  { x: 634, y: -48, ex: 602, ey: 1160, w: 9.6, h: 14.4, rot: 6, color: "#FAC775" },
  { x: 801, y: -62, ex: 841, ey: 1168, w: 6.4, h: 9.6, rot: -41, color: "#EF9F27" },
  { x: 968, y: -20, ex: 926, ey: 1136, w: 8, h: 12, rot: 92, color: "#E24B4A" },
  { x: 1135, y: -34, ex: 1165, ey: 1148, w: 9.6, h: 14.4, rot: 45, color: "#FAC775" },
  { x: 422, y: -48, ex: 392, ey: 1160, w: 6.4, h: 9.6, rot: -2, color: "#EF9F27" },
  { x: 589, y: -62, ex: 629, ey: 1166, w: 8, h: 12, rot: -49, color: "#E24B4A" },
  { x: 756, y: -20, ex: 712, ey: 1136, w: 9.6, h: 14.4, rot: 84, color: "#FAC775" },
  { x: 923, y: -34, ex: 957, ey: 1148, w: 6.4, h: 9.6, rot: 37, color: "#EF9F27" },
  { x: 1090, y: -48, ex: 1060, ey: 1159, w: 8, h: 12, rot: -10, color: "#E24B4A" },
  { x: 377, y: -62, ex: 417, ey: 1164, w: 9.6, h: 14.4, rot: -57, color: "#FAC775" },
  { x: 544, y: -20, ex: 505, ey: 1136, w: 6.4, h: 9.6, rot: 76, color: "#EF9F27" },
  { x: 711, y: -34, ex: 745, ey: 1148, w: 8, h: 12, rot: 29, color: "#E24B4A" },
  { x: 878, y: -48, ex: 848, ey: 1157, w: 9.6, h: 14.4, rot: -18, color: "#FAC775" },
  { x: 1045, y: -62, ex: 1085, ey: 1166, w: 6.4, h: 9.6, rot: -65, color: "#EF9F27" },
];
const CANVAS_W = 1440;
const CANVAS_H = 1206;

const petalFall = (p, i) => keyframes`
  0%   { left: ${(p.x / CANVAS_W) * 100}%; top: ${(p.y / CANVAS_H) * 100}%; transform: rotate(0deg); opacity: 0; }
  8%   { opacity: 1; }
  45%  { left: ${(((p.x + p.ex) / 2 + (i % 2 ? 24 : -24)) / CANVAS_W) * 100}%; }
  80%  { opacity: 1; }
  100% { left: ${(p.ex / CANVAS_W) * 100}%; top: ${(p.ey / CANVAS_H) * 100}%; transform: rotate(${p.rot + 360}deg); opacity: 0; }
`;

const motionSafe = (styles) => ({ "@media (prefers-reduced-motion: no-preference)": styles });

// The temple, sun and birds — sits at the top of the hero.
// height: optional pixel height (the page fits the temple to the window); width follows the 760×420 ratio.
export const SkylineTemple = ({ height }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: "relative",
      width: height ? `min(${Math.round((height * W) / H)}px, 92vw)` : `min(${W}px, 92vw)`,
      transition: "width 0.2s ease",
      aspectRatio: `${W} / ${H}`,
      mx: "auto",
    }}
  >
    {/* Sun (192px at x 284, final top -26 relative to the temple) */}
    <Box
      component="img"
      src={sun}
      alt=""
      sx={{
        position: "absolute",
        width: `${(192 / W) * 100}%`,
        left: `${(284 / W) * 100}%`,
        top: `${(-26 / H) * 100}%`,
        ...motionSafe({ animation: `${sunRise} 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) both` }),
      }}
    />
    {/* Birds */}
    <Box
      sx={{
        position: "absolute",
        top: `${(60 / H) * 100}%`,
        left: "121%",
        width: `${(80 / W) * 100}%`,
        aspectRatio: "80 / 30",
        opacity: 0,
        ...motionSafe({ animation: `${birdsFly} 4.4s ease-in-out 0.3s both` }),
      }}
    >
      <svg viewBox="0 0 80 30" width="100%" height="100%" fill="none">
        <path d="M2 12C6 8 10 8 14 12C18 8 22 8 26 12" stroke="#C9952B" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M44 24C47.3 20.7 50.7 20.7 54 24C57.3 20.7 60.7 20.7 64 24" stroke="#C9952B" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </Box>
    {/* Line art, revealed left to right */}
    <Box
      component="img"
      src={skylineLines}
      alt=""
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        ...motionSafe({ animation: `${lineReveal} 1.6s cubic-bezier(0.65, 0, 0.35, 1) 0.2s both` }),
      }}
    />
    {/* Coloured temple fades in over the lines */}
    <Box
      component="img"
      src={skylineFill}
      alt=""
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        ...motionSafe({ animation: `${fadeIn} 0.9s ease-out 1.7s both` }),
      }}
    />
  </Box>
);

// Petals fall across the whole hero (the parent must be position: relative).
export const PetalShower = () => (
  <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 2, "@media (prefers-reduced-motion: reduce)": { display: "none" } }}>
    {PETALS.map((p, i) => (
      <Box
        key={i}
        sx={{
          position: "absolute",
          width: `${p.w}px`,
          height: `${p.h}px`,
          borderRadius: "50%",
          background: p.color,
          opacity: 0,
          animation: `${petalFall(p, i)} ${2.6 + (i % 4) * 0.25}s cubic-bezier(0.37, 0, 0.63, 1) ${2.3 + (i % 6) * 0.12}s both`,
        }}
      />
    ))}
  </Box>
);
